import type { StoreType } from '@/types/store';
import type {
  Study,
  StudyArm,
  BioavailabilityResult,
  FormulaBreakdown,
  ValidationWarning,
  PkParameters,
  AucResult,
  BioequivalenceResult,
} from '@bioavail/shared-types';
import { WarningSeverity as WS } from '@bioavail/shared-types';
import { checkQuota } from '@/persistence/quotaGuard';
import type { PersistedState } from '@/types/persistence';

type State = Pick<
  StoreType,
  | 'studies'
  | 'activeStudyId'
  | 'output'
  | 'settings'
  | 'trail'
  | 'dataset'
  | 'dismissedWarnings'
>;

export function selectActiveStudy(state: State): Study | null {
  return state.studies.find((s) => s.id === state.activeStudyId) ?? null;
}

export function selectArms(state: State): StudyArm[] {
  return selectActiveStudy(state)?.arms ?? [];
}

export function selectArmsSorted(state: State): StudyArm[] {
  const arms = selectArms(state);
  return [...arms].sort((a, b) => {
    if (a.role === 'test' && b.role !== 'test') return -1;
    if (a.role !== 'test' && b.role === 'test') return 1;
    return 0;
  });
}

export function selectResult(state: State): BioavailabilityResult | null {
  return state.output?.bioavailability ?? null;
}

export function selectFormula(state: State): FormulaBreakdown | null {
  return state.output?.bioavailability?.formula ?? null;
}

export function selectWarnings(state: State): ValidationWarning[] {
  const dismissed = state.dismissedWarnings;
  const all = state.output?.warnings ?? [];
  const filtered = all.filter((w) => w.severity === WS.Block || !dismissed.includes(w.code));
  const rank: Record<string, number> = { block: 0, warn: 1, info: 2 };
  return [...filtered].sort((a, b) => (rank[a.severity] ?? 3) - (rank[b.severity] ?? 3));
}

export function selectBlockWarnings(state: State): ValidationWarning[] {
  return selectWarnings(state).filter((w) => w.severity === WS.Block);
}

export function selectCanCompute(state: State): boolean {
  return selectBlockWarnings(state).length === 0 && selectArms(state).length === 2;
}

export function selectPkParameters(state: State): PkParameters[] {
  return state.output?.pkParameters ?? [];
}

export function selectAucResults(state: State): AucResult[] {
  return state.output?.aucResults ?? [];
}

export function selectBioequivalence(state: State): BioequivalenceResult | null {
  return state.output?.bioequivalence ?? null;
}

export interface ChartArm {
  armId: string;
  label: string;
  role: string;
  color: string;
  points: { time: number; concentration: number }[];
}

const memoCache = new WeakMap<object, ChartArm[]>();

export function selectChartArms(state: State): ChartArm[] {
  const arms = selectArms(state);
  const cached = memoCache.get(arms);
  if (cached) return cached;

  const result: ChartArm[] = arms.map((arm) => ({
    armId: arm.id,
    label: arm.label,
    role: arm.role,
    color: arm.role === 'test' ? 'var(--color-chart-test)' : 'var(--color-chart-reference)',
    points: arm.usesDirectAuc
      ? []
      : arm.dataPoints.map((p) => ({ time: p.time, concentration: p.concentration })),
  }));
  memoCache.set(arms, result);
  return result;
}

export function selectTheme(state: State): 'light' | 'dark' {
  const theme = state.settings.theme;
  if (theme !== 'system') return theme;
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

export function selectStorageUsed(state: State): {
  used: number;
  quota: number;
  percent: number;
} {
  const persisted: PersistedState = {
    version: 1,
    activeStudyId: state.activeStudyId,
    studies: state.studies,
    settings: state.settings,
    ...(state.dataset ? { dataset: state.dataset } : {}),
    auditTrail: state.trail,
  };
  const guard = checkQuota(persisted);
  return { used: guard.used, quota: guard.quota, percent: guard.percent };
}

export function selectInterpretation(state: State): string | null {
  const result = selectResult(state);
  if (!result) return null;
  return result.interpretation || null;
}
